import type { EntityState } from '@reduxjs/toolkit';
import {
	createAsyncThunk,
	createEntityAdapter,
	createSlice,
} from '@reduxjs/toolkit';
import { AvailabilityResultState } from '~/domain';
import { ApiRequestError, ApiServerError, fetch2, unwrapFetch2 } from '~/fetch';
import { endpoint } from '~/helpers/Endpoints';
import type { IAppState } from '~/appstate';
import type { IProductAvailability } from './schema';
import { productAvailabilityId } from './schema';

export const productAvailabilitiesAdapter =
	createEntityAdapter<IProductAvailability>({
		selectId: productAvailabilityId,
	});

interface IFetchProductAvailabilityResponse {
	supplierProductNumber: string;
	unitOfMeasure: string;
	isAvailable: boolean;
}

export interface IProductAvailabilityRequest {
	blueprintId: number;
	locationCode: string;
	products: IProductNumberAndUM[];
}

export interface IProductNumberAndUM {
	supplierProductNumber: string;
	unitOfMeasure: string;
}

export interface IProductAvailabilityResponse {
	status: AvailabilityResultState;
	productAvailabilities: IProductAvailability[];
}

export const fetchProductAvailabilities = createAsyncThunk<
	IProductAvailabilityResponse,
	IProductAvailabilityRequest,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'productAvailabilities/fetchProductAvailabilities',
	async (
		params: IProductAvailabilityRequest,
		{ getState, rejectWithValue }
	) => {
		try {
			const { blueprintId, locationCode } = params;
			const state = getState();

			const requestProducts = params.products.filter(
				(product) =>
					!state.productAvailabilities?.ids?.includes(
						[blueprintId, locationCode, product.supplierProductNumber].join('|')
					)
			);

			if (requestProducts == null || requestProducts.length === 0) {
				return {
					status: AvailabilityResultState.Full,
					productAvailabilities: [],
				};
			}

			const result = await unwrapFetch2(
				fetch2<IFetchProductAvailabilityResponse[]>({
					uri: endpoint.productAvailability(blueprintId, locationCode),
					method: 'POST',
					body: JSON.stringify(requestProducts),
				})
			);

			const productAvailabilities = result.map((product) => ({
				blueprintId,
				locationCode,
				supplierProductNumber: product.supplierProductNumber,
				isAvailable: product.isAvailable,
			}));

			const status =
				result.length === 0
					? AvailabilityResultState.None
					: requestProducts.some(
							(p) =>
								!result.find(
									(r) => r.supplierProductNumber === p.supplierProductNumber
								)
					  )
					? AvailabilityResultState.Partial
					: AvailabilityResultState.Full;

			return {
				status,
				productAvailabilities,
			};
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export type IProductAvailabilitiesState = EntityState<IProductAvailability>;

export const productAvailabilitiesSlice = createSlice({
	name: 'productAvailabilities',
	initialState: productAvailabilitiesAdapter.getInitialState(),
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(fetchProductAvailabilities.fulfilled, (state, action) => {
			productAvailabilitiesAdapter.upsertMany(
				state,
				action.payload.productAvailabilities
			);
		});
	},
});
